import {
  ContentItem,
  ContentItems,
  ContentWrapper,
  Flex,
  Table,
  TitledText,
} from '@/components';
import { ReviewSheetModel } from '@/features/project/projects/_models/project';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { ProjectInformationHeader } from './ProjectInformationHeader';
import {
  LegalBasisPinpointItem,
  ProjectOverviewPinpointItem,
  ProjectPurposePinpointItem,
  ProjectReferencePinpointItem,
  ProjectRelatedProjectsPinpointItem,
  ProjectTaskPinpointItem,
} from './pinpoint-items/basic-information-pinpoint-fields';

type Props = z.infer<typeof ReviewSheetModel> & {
  historySavedAt: string | undefined;
};

export const RsProjectInformation: FC<Props> = (props) => {
  const {
    id,
    fiscalYear,
    organizationId,
    relatedProjects,
    relatedOrganizationIds,
    otherResponsibleOrganizationIds,
    projectNumber,
    oldProjectNumber,
    startYear,
    endYear,
    isUnknownStart,
    isNoScheduledEnd,
    category,
    purpose,
    overview,
    overviewUrl,
    legalBases,
    plans,
    policiesForProject,
    task,
    mainCosts,
    remark,
    sortOrder,
    name,
    sheetType,
    ...rest
  } = ReviewSheetModel.parse(props);
  const { t } = useTranslation();

  return (
    <>
      <ProjectInformationHeader
        ministryId={rest.ministryId}
        historySavedAt={props.historySavedAt}
        organizationId={organizationId}
        relatedOrganizationIds={relatedOrganizationIds}
        otherResponsibleOrganizationIds={otherResponsibleOrganizationIds}
        peopleInChargeIds={rest.peopleInChargeIds ?? null}
        businessManagerIds={rest.businessManagerIds}
        pinpointSheetType="RS"
        projectNumber={projectNumber}
      />
      <ContentWrapper>
        <ContentItem borderBottom={0}>
          <ContentItems
            items={[
              <ProjectReferencePinpointItem
                key="project-reference"
                projectId={id}
                pinpointSheetType={sheetType}
                projectReference={rest.indonesiaData?.acuanProgram}
              />,
              <LegalBasisPinpointItem
                key="legal-basis"
                projectId={id}
                pinpointSheetType={sheetType}
                legalBasis={rest.indonesiaData?.dasarHukumAtauKebijakan}
              />,
            ]}
          />
        </ContentItem>
      </ContentWrapper>

      <ContentWrapper title={t('project-Alasan-dan-Tujuan-Review')}>
        <ContentItem borderBottom={0}>
          <ContentItems
            items={[
              <TitledText
                key="project-alasan-review"
                title={t('project-alasan-review')}
                text={rest.indonesiaData?.alasanReview}
              />,
              <TitledText
                key="project-tujuan-review"
                title={t('project-tujuan-review')}
                text={rest.indonesiaData?.tujuanReview}
              />,
            ]}
          />
        </ContentItem>
      </ContentWrapper>

      <ContentWrapper title={t('project-Ringkasan-Program')}>
        <ContentItem borderBottom="0">
          <Flex vertical gap={24} style={{ width: '100%' }}>
            <ContentItems
              items={[
                <ProjectTaskPinpointItem
                  key="project-task"
                  projectId={id}
                  task={task}
                  pinpointSheetType={sheetType}
                />,
                <ProjectPurposePinpointItem
                  key="project-purpose"
                  projectId={id}
                  purpose={purpose}
                  pinpointSheetType={sheetType}
                />,
              ]}
            />
            <ContentItems
              items={[
                <ProjectOverviewPinpointItem
                  key="overview"
                  projectId={id}
                  pinpointSheetType={sheetType}
                  overview={overview}
                  overviewUrl={overviewUrl}
                />,
                <ProjectRelatedProjectsPinpointItem
                  key="related-projects"
                  projectId={id}
                  relatedProjects={relatedProjects}
                  pinpointSheetType={sheetType}
                />,
              ]}
            />
            <ContentItems
              items={[
                <TitledText
                  key="project-target"
                  title={t('project-target')}
                  text={rest.indonesiaData?.target}
                />,
              ]}
            />
            <Table
              bordered
              dataSource={rest.indonesiaData?.jumlahTargetPenerimaManfaat ?? []}
              columns={[
                {
                  title: t('value'),
                  dataIndex: 'elementValue',
                },
                {
                  title: t('unit'),
                  dataIndex: 'elementUnit',
                },
                {
                  title: t('value'),
                  dataIndex: 'element2Value',
                },
                {
                  title: t('unit'),
                  dataIndex: 'element2Unit',
                },
                {
                  title: t('ratio'),
                  dataIndex: 'ratio',
                },
              ]}
            />
            {/* <Row gutter={[16, 16]}>
              {rest.indonesiaData?.jumlahTargetPenerimaManfaat?.map(
                (item, idx) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                  <Col key={idx} span={12}>
                    <Card
                      styles={{
                        body: {
                          gap: 24,
                          display: 'flex',
                          width: '100%',
                        },
                      }}
                    >
                      <TitledText title={t('value')} text={item.elementValue} />
                      <TitledText title={t('unit')} text={item.elementUnit} />
                      <TitledText
                        title={t('value')}
                        text={item.element2Value}
                      />
                      <TitledText title={t('unit')} text={item.element2Unit} />
                      <TitledText
                        title={t('ratio')}
                        text={
                          item.ratio ? formatPercentage(item.ratio / 100) : '0%'
                        }
                      />
                    </Card>
                  </Col>
                ),
              )}
            </Row> */}
          </Flex>
        </ContentItem>
      </ContentWrapper>
    </>
  );
};
